import * as React from 'react';
import * as Utils from './utils/DataFinderUtils';
import styled from 'styled-components';
import LoadingPage from '../LoadingPage';
import { rem } from '../../utils/styles';
import StylovyzeSearchBar from '../StylovyzeSearchBar';
import WidgetError from '../WidgetError';
import { useGlobalization } from '../../contexts';
import EmptyTableBody from '../EmptyTableBody';

// # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # #
// # # # Types
// # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # #

/**
 * The data for the data finder is a list of `Category` entities.
 */
export type Data = {
	categories?: Category[];
};

/**
 * A category is a list of `Options` of the same type at the same level of the hierarchy.
 * This is used in rendering the DataFinder component.
 *
 * @param id the id of the category
 * @param label the human readable text displayed for the category
 * @param options the list of available options for the category
 * @param isLoading a flag indicating if the category is still loading
 * @param initialized a flag indicating if the category has been initialized (either loading, complete, or errored)
 * @param searchable a flag indicating if the category should be searchable
 * @param error a string containing an error message if the category failed to load
 */
export type Category = {
	id: string;
	label: string;
	options?: Option[];
	isLoading: boolean;
	initialized?: boolean;
	searchable?: boolean;
	error?: string | null;
};

/**
 * An option is a single item in a category.  This is used in rendering the DataFinder component.
 *
 * @param id the id of the option
 * @param label the human readable text displayed for the option
 */
export type Option = {
	id: string;
	label: string;
};

/**
 * A selection identifies a single option from a single category.  This is used in rendering the DataFinder component.
 * Compare to `SimulationDataObject` in `SimulationDataFinderDialog.tsx`.
 *
 * @param optionId the id of the selected option
 * @param categoryId the id of the category the option belongs to
 */
export type Selection = { optionId: string; categoryId: string };

// # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # #
// # # # List Item Wrapper Component
// # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # #

const _ListItem: React.FunctionComponent<
	Option & {
		categoryId: string;
		selections: Selection[];
		onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
	}
> = props => {
	const isSelected = Utils.isItemSelected(
		props.selections,
		props.categoryId,
		props.id,
	);

	const listItemRef = React.useRef<HTMLDivElement>(null);
	const isSelectedRef = React.useRef(isSelected);

	React.useLayoutEffect(() => {
		if (isSelectedRef.current) {
			listItemRef.current?.scrollIntoView();
		}
	}, []);

	return (
		<ListItem
			ref={listItemRef}
			selected={isSelected}
			onClick={props.onClick}>
			{props.label}
		</ListItem>
	);
};

_ListItem.displayName = 'DataFinder._ListItem';

// # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # #
// # # # Root Component
// # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # #

export type RootProps = {
	children?: React.ReactNode;
	data: Data;
	selections: Selection[];
	onSelection: (selection: Selection) => void;
	multiSelect?: Partial<{ [key: string]: boolean }>;
};

const SearchBar = styled(StylovyzeSearchBar)`
	margin-right: 13px;
	margin-left: 8px;
	margin-top: 10px;
`;

export const ErrorWrapper = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	height: 100%;
	margin: auto;
`;

/**
 * The properties for a `CategoryList` component.
 *
 * @param category the category to display
 * @param selections the current selections
 * @param onSelection the callback to call when a selection is made
 */
interface CategoryListProps {
	category: Category;
	selections: Selection[];
	onSelection: (selection: Selection) => void;
}

/**
 * A list of options for a single category displayed vertically.  This component
 * also displays a search bar if the category is searchable.  If the category is
 * still loading, an indeterminate progress bar is displayed.  If an error occurred
 * while loading the category, an error message is displayed.
 *
 * @param props the properties for the component see `CategoryListProps`
 * @returns a React `CategoryList` component
 */
const CategoryList = (props: CategoryListProps) => {
	const { t } = useGlobalization();
	const { category, selections, onSelection } = props;
	const [searchTerm, setSearchTerm] = React.useState('');

	if (category.isLoading) {
		return (
			<>
				{category.searchable && (
					<SearchBar
						debounce
						debounceTime={0}
						handleSearchSubmit={value => setSearchTerm(value)}
					/>
				)}
				<LoadingPage />
			</>
		);
	}
	if (category.error) {
		return (
			<ErrorWrapper>
				<WidgetError
					message={t('There was an error loading this information')}
				/>
			</ErrorWrapper>
		);
	}
	if (category.initialized && category.options?.length === 0) {
		return (
			<EmptyTableBody title={t('No data')} subtitle={category.label} />
		);
	}
	return (
		<>
			{category.searchable && category.initialized && (
				<SearchBar
					debounce
					debounceTime={0}
					handleSearchSubmit={value => setSearchTerm(value)}
				/>
			)}
			{category.options
				?.filter(opt => opt.label.includes(searchTerm))
				.map(option => {
					const categoryId = category.id;
					const optionId = option.id;

					return (
						<_ListItem
							{...option}
							key={optionId}
							selections={selections}
							categoryId={categoryId}
							onClick={() =>
								onSelection({
									categoryId,
									optionId,
								})
							}
						/>
					);
				})}{' '}
		</>
	);
};

/**
 * The `Root` component collects all the `CategoryList` components and displays
 * them horizontally along with their labels.
 */
export const Root = React.forwardRef<HTMLDivElement, RootProps>(
	(props, ref) => {
		const { selections, onSelection, data } = props;
		return (
			<RootContainer ref={ref}>
				{data.categories?.length
					? data.categories.map(category => {
							return (
								<List key={category.id}>
									<ListHeader>{category.label}</ListHeader>
									<ListItems data-cy={`list-${category.id}`}>
										<CategoryList
											category={category}
											selections={selections}
											onSelection={onSelection}
										/>
									</ListItems>
								</List>
							);
					  })
					: null}
			</RootContainer>
		);
	},
);

Root.displayName = 'DataFinder.Root';

export const RootContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: stretch;
	gap: 8px;

	height: 100%;
	width: 100%;
	background-color: #f7f7f7;
	overflow: hidden;
`;

RootContainer.displayName = 'DataFinder.RootContainer';

// # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # #
// # # # List
// # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # # #

export const List = styled.article`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: stretch;
	flex: 1;
	overflow: hidden;
`;

List.displayName = 'DataFinder.List';

export const ListHeader = styled.header`
	font-size: 0.8rem;
	font-weight: bolder;
	padding: 12px 24px;

	background-color: transparent;
`;

List.displayName = 'DataFinder.ListHeader';

export const ListItems = styled.div`
	flex: 1;

	min-height: ${rem(340)};
	max-height: ${rem(340)};
	background-color: #fff;
	overflow: auto;
`;

ListItems.displayName = 'DataFinder.ListItems';

/**
 * properties for a ListItem component.
 *
 * @param selected a flag indicating if the item is selected
 */
export type ListItemProps = {
	selected?: boolean;
};

// TODO: Replace div with another element that makes more sense
// TODO: Properly set 'selected' state with ARIA roles
/**
 * A styled component for a list item in the DataFinder component.
 */
export const ListItem = styled.div<ListItemProps>`
	background-color: ${p =>
		p.selected ? 'rgb(167, 236, 255, 0.5)' : 'transparent'};
	cursor: pointer;
	overflow: hidden;
	padding: 8px 24px;
	text-overflow: ellipsis;
	white-space: nowrap;
	width: 100%;

	&:hover {
		background-color: rgb(167, 236, 255, 0.2);
	}
`;

ListItem.displayName = 'DataFinder.ListItem';
